import { useEffect, useState } from 'react';
/// <reference types="../types/css-modules" />
import style from '../styles/AccountEdit.module.css';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import { useBannerStore } from '../utils/banner';
import { useAlertStore } from '../utils/alert';
import { useAuth } from '../contexts/AuthContext';

/**
 * Page component for editting a venue owner account
 * Retrieves current account data and autofills relevant fields
 * Calls global alert for response messages
 * @returns Venue Owner Account Edit page component
 */
export function VenueOwnerAccountEdit() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [formData, setFormData] = useState({
    email: null as string | null,
    tel: null as string | null,
    username: null as string | null,
    profilePictureUrl: null as string | null,
  });

  const handleFormData = (field: string, data: string) => {
    setFormData((oldData) => ({ ...oldData, [field]: data }));
  };

  const profileQuery = trpc.venueOwner.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });

  const updateProfile = trpc.venueOwner.updateMyProfile.useMutation({
    onSuccess: (data) => {
      if (data.updated) {
        useAlertStore.getState().showAlert({
          header: 'Success',
          body: 'Your profile has been updated.',
          button: 'Ok',
        });
        profileQuery.refetch();
      }
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: 'Error updating profile',
        button: 'Ok',
      });
    },
  });

  const fallbackProfilePictureUrl =
    formData.profilePictureUrl ?? profileQuery.data?.profile_picture_url ?? '';

  const showBanner = useBannerStore((state) => state.showBanner);
  const clearBanner = useBannerStore((state) => state.clearBanner);
  useEffect(() => {
    showBanner({
      icon: fallbackProfilePictureUrl,
      header: 'YOUR ACCOUNT',
      buttons: {
        View: '/venue-owner/profile',
        Edit: '/venue-owner/edit',
        Settings: '/venue-owner/settings/settings',
      },
      selected: 'Edit',
    });
    return () => clearBanner();
  }, [fallbackProfilePictureUrl, showBanner, clearBanner]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updates: {
      username?: string;
      profile_picture_url?: string;
    } = {};

    const username = formData.username ?? profileQuery.data?.username ?? '';
    const profilePictureUrl =
      formData.profilePictureUrl ?? profileQuery.data?.profile_picture_url ?? '';

    if (username.trim()) updates.username = username.trim();
    if (profilePictureUrl.trim()) updates.profile_picture_url = profilePictureUrl.trim();

    updateProfile.mutate(updates);
  };

  if (profileQuery.isLoading) {
    return <div style={{ padding: '2rem' }}>Loading...</div>;
  }

  return (
    <div className="container">
      <form className="form" onSubmit={(e) => handleProfileSubmit(e)}>
        <div className={style.profileContainer}>
          <ProfilePictureUpload
            currentUrl={fallbackProfilePictureUrl || null}
            userId={session?.user?.id || ''}
            onUploadComplete={(url) => {
              handleFormData('profilePictureUrl', url);
              updateProfile.mutate({ profile_picture_url: url });
            }}
            size={120}
            fallbackLetter={(profileQuery.data?.username || '?').charAt(0)}
          />
        </div>

        <div className="fieldContainer">
          <div className="fieldWithLabel">
            <label htmlFor="Username"> Username </label>
            <input
              id="Username"
              name="Username"
              value={formData.username ?? profileQuery.data?.username ?? ''}
              className="field"
              onChange={(data) => handleFormData('username', data.target.value)}
            ></input>
          </div>
        </div>

        <div className="fieldContainer">
          <div className="fieldWithLabel">
            <label htmlFor="Email"> Email </label>
            <input
              id="Email"
              name="Email"
              value={formData.email ?? session?.user?.email ?? ''}
              className="field"
              onChange={(data) => handleFormData('email', data.target.value)}
            ></input>
          </div>
        </div>

        <div className="fieldContainer">
          <div className="fieldWithLabel">
            <label htmlFor="Phone"> Phone </label>
            <input
              id="Phone"
              name="Phone"
              value={formData.tel ?? session?.user?.phone ?? ''}
              className="field"
              onChange={(data) => handleFormData('tel', data.target.value)}
            ></input>
          </div>
        </div>

        <div className={style.buttonContainer}>
          <button type="button" onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
          <button type="submit"> Submit </button>
        </div>
      </form>
    </div>
  );
}
